import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import FilterListIcon from '@mui/icons-material/FilterList';
import * as departmentApi from '../../api/departmentApi';
import * as materialGroupApi from '../../api/materialGroupApi';
import * as accessoryGroupApi from '../../api/accessoryGroupApi';

/**
 * Reusable filter dialog shown before triggering an Excel download.
 *
 * Props:
 *   open       – boolean
 *   title      – dialog title string
 *   onClose    – called when user cancels
 *   onConfirm  – called with filter object { departmentId?, departmentName?, groupCode? }
 *                Pass empty object {} when "All Records" is selected.
 */
/**
 * groupType — 'material' (default) | 'accessory'
 * Determines which group API is called to populate the Group Code dropdown.
 */
export default function ExcelDownloadFilterDialog({ open, title, onClose, onConfirm, groupType = 'material' }) {
  const [filterType, setFilterType]           = useState('all');       // 'all' | 'department' | 'groupCode'
  const [departments, setDepartments]         = useState([]);
  const [groups, setGroups]                   = useState([]);
  const [selectedDeptId, setSelectedDeptId]   = useState('');
  const [selectedGroupCode, setSelectedGroupCode] = useState('');
  const [loadingDepts, setLoadingDepts]       = useState(false);
  const [loadingGroups, setLoadingGroups]     = useState(false);

  // Load departments when dialog opens; reset all selections
  useEffect(() => {
    if (!open) return;
    setFilterType('all');
    setSelectedDeptId('');
    setSelectedGroupCode('');
    setGroups([]);
    setLoadingDepts(true);
    departmentApi.getAll()
      .then((res) => setDepartments(Array.isArray(res) ? res : (res.data || [])))
      .catch(() => setDepartments([]))
      .finally(() => setLoadingDepts(false));
  }, [open]);

  // When in groupCode mode, reload groups whenever department selection changes
  useEffect(() => {
    if (filterType !== 'groupCode') {
      setGroups([]);
      setSelectedGroupCode('');
      return;
    }
    setLoadingGroups(true);
    const params = selectedDeptId ? { departmentId: selectedDeptId } : {};
    const groupApi = groupType === 'accessory' ? accessoryGroupApi : materialGroupApi;
    groupApi.getAll(params)
      .then((res) => {
        setGroups(Array.isArray(res) ? res : (res.data || []));
        setSelectedGroupCode('');
      })
      .catch(() => setGroups([]))
      .finally(() => setLoadingGroups(false));
  }, [filterType, selectedDeptId]);

  const handleFilterTypeChange = (e) => {
    setFilterType(e.target.value);
    setSelectedDeptId('');
    setSelectedGroupCode('');
  };

  const isConfirmDisabled =
    (filterType === 'department' && !selectedDeptId) ||
    (filterType === 'groupCode'  && !selectedGroupCode);

  const handleConfirm = () => {
    const filter = {};
    if (filterType === 'department' && selectedDeptId) {
      filter.departmentId   = selectedDeptId;
      const dept = departments.find((d) => d.id === Number(selectedDeptId));
      filter.departmentName = dept?.name || String(selectedDeptId);
    } else if (filterType === 'groupCode' && selectedGroupCode) {
      filter.groupCode = selectedGroupCode;
      if (selectedDeptId) {
        filter.departmentId   = selectedDeptId;
        const dept = departments.find((d) => d.id === Number(selectedDeptId));
        filter.departmentName = dept?.name || String(selectedDeptId);
      }
    }
    onConfirm(filter);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <FilterListIcon fontSize="small" color="primary" />
        {title || 'Download Excel'}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ fontSize: '0.8rem', mb: 1, fontWeight: 600 }}>
            Filter records to include
          </FormLabel>

          <RadioGroup value={filterType} onChange={handleFilterTypeChange}>

            {/* ── ALL ──────────────────────────────────────────────── */}
            <FormControlLabel
              value="all"
              control={<Radio size="small" />}
              label={<Typography variant="body2">All Records</Typography>}
            />

            {/* ── DEPARTMENT ───────────────────────────────────────── */}
            <FormControlLabel
              value="department"
              control={<Radio size="small" />}
              label={<Typography variant="body2">By Department</Typography>}
            />
            {filterType === 'department' && (
              <Box sx={{ pl: 3.5, mb: 1 }}>
                {loadingDepts ? (
                  <CircularProgress size={18} />
                ) : (
                  <FormControl size="small" fullWidth>
                    <InputLabel>Select Department</InputLabel>
                    <Select
                      value={selectedDeptId}
                      onChange={(e) => setSelectedDeptId(e.target.value)}
                      label="Select Department"
                    >
                      {departments.map((d) => (
                        <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}

            {/* ── GROUP CODE ────────────────────────────────────────── */}
            <FormControlLabel
              value="groupCode"
              control={<Radio size="small" />}
              label={<Typography variant="body2">By Group Code</Typography>}
            />
            {filterType === 'groupCode' && (
              <Box sx={{ pl: 3.5, display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1 }}>
                {/* Optional department pre-filter to narrow the group code list */}
                {loadingDepts ? (
                  <CircularProgress size={18} />
                ) : (
                  <FormControl size="small" fullWidth>
                    <InputLabel>Department (optional)</InputLabel>
                    <Select
                      value={selectedDeptId}
                      onChange={(e) => setSelectedDeptId(e.target.value)}
                      label="Department (optional)"
                    >
                      <MenuItem value=""><em>All Departments</em></MenuItem>
                      {departments.map((d) => (
                        <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {loadingGroups ? (
                  <CircularProgress size={18} />
                ) : (
                  <FormControl size="small" fullWidth>
                    <InputLabel>Group Code</InputLabel>
                    <Select
                      value={selectedGroupCode}
                      onChange={(e) => setSelectedGroupCode(e.target.value)}
                      label="Group Code"
                      disabled={groups.length === 0}
                    >
                      {groups.length === 0 && (
                        <MenuItem value="" disabled><em>No group codes found</em></MenuItem>
                      )}
                      {groups.map((g) => (
                        <MenuItem key={g.id} value={g.groupCode}>
                          {g.groupCode}
                          {g.department?.name && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                              ({g.department.name})
                            </Typography>
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}

          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit" variant="outlined" size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}
